import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
    children: React.ReactNode;
    content: string | React.ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    trigger?: 'hover' | 'click' | 'focus';
    delay?: number;
    className?: string;
    disabled?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
    children,
    content,
    placement = 'top',
    trigger = 'hover',
    delay = 200,
    className = '',
    disabled = false
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const placements = {
        top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
        left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
        right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
    };

    const arrows = {
        top: 'top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-[var(--bg-elevated)]',
        bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-[var(--bg-elevated)]',
        left: 'left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-[var(--bg-elevated)]',
        right: 'right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-[var(--bg-elevated)]'
    };

    const showTooltip = () => {
        if (disabled) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    };

    const hideTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setIsVisible(false);
        }, 100);
    };

    const handleClick = () => {
        if (disabled) return;
        setIsVisible(!isVisible);
    };

    const handleFocus = () => {
        if (disabled) return;
        showTooltip();
    };

    const handleBlur = () => {
        hideTooltip();
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const triggerProps = {
        ...(trigger === 'hover' && {
            onMouseEnter: showTooltip,
            onMouseLeave: hideTooltip
        }),
        ...(trigger === 'click' && {
            onClick: handleClick
        }),
        ...(trigger === 'focus' && {
            onFocus: handleFocus,
            onBlur: handleBlur,
            tabIndex: 0
        })
    };

    return (
        <div ref={triggerRef} className={`relative inline-block ${className}`}>
            <div {...triggerProps}>
                {children}
            </div>

            {isVisible && !disabled && (
                <div
                    ref={tooltipRef}
                    className={`
                        absolute z-50 px-3.5 py-2 text-[13px] font-medium
                        bg-[var(--bg-elevated)] text-[var(--text-primary)]
                        border border-[var(--border-glass)]
                        rounded-[var(--radius-md)] shadow-[var(--shadow-premium)] max-w-xs
                        backdrop-blur-xl
                        animate-in fade-in zoom-in-95 duration-[var(--duration-fast)]
                        ${placements[placement]}
                    `}
                    style={{ whiteSpace: 'nowrap' }}
                    role="tooltip"
                >
                    {content}
                    {/* Arrow */}
                    <div
                        className={`
                            absolute w-0 h-0 border-4
                            ${arrows[placement]}
                        `}
                    />
                </div>
            )}
        </div>
    );
};

export default Tooltip;
